import Editor from '@monaco-editor/react';
import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabPanel, TabView } from 'primereact/tabview';
import React, { Suspense, useEffect, useState } from 'react';
import { isJSON } from "utils/utils";
import { ComponentTypeWithID, getDashboardComponentTypes } from '../ComponentType';

import 'style/overrides.scss';
import { easasAlert } from './easas-global-fragment/EasasGlobalAlerts';

type ConfigurationModel = {
    title: string;
    width: number | null;
    componentType: string | undefined;
    jsonConfig: string | undefined;
};

type ConfiguratorProps = {
    widgetVar: string;
    model: ConfigurationModel;
};

const DashboardComponentConfigurator: React.FC<ConfiguratorProps> = (props) => {
    const componentTypes: ComponentTypeWithID[] = getDashboardComponentTypes();
    const jsonInputEl = document.getElementById(PF(props.widgetVar).id) as HTMLInputElement;
    const [configuratorState, setConfiguratorState] = useState<ConfigurationModel>({
        title: props.model.title,
        width: props.model.width,
        componentType: props.model.componentType,
        jsonConfig: props.model.jsonConfig,
    });
    
    useEffect(() => {
        jsonInputEl.value = JSON.stringify(configuratorState);
    }, [configuratorState]);

    const onJSONChange = (json: object) => {
        setConfiguratorState((prevState) => ({...prevState, jsonConfig: JSON.stringify(json)}));
    };

    const currentComponentType = componentTypes.find((element) => element.id === configuratorState.componentType);
    const SelectedComponent = currentComponentType?.editorEntryPoint;

    const componentSelectorHandler = (componentType: string) => {
        const json = configuratorState.jsonConfig?.length !== undefined && configuratorState.jsonConfig?.length > 2 ? configuratorState.jsonConfig : '{}'
        setConfiguratorState((prevState) => ({
            ...prevState,
            componentType,
            jsonConfig: json
        }))
    }

    const [activeIndex, setActiveIndex] = useState(0);

    const formatJSON = () => {
        try {
            const parsed = JSON.parse(configuratorState.jsonConfig as string);
            const formatted = JSON.stringify(parsed, null, 4);
            setConfiguratorState((prevState) => ({...prevState, jsonConfig: formatted}));
        } catch (error) {
            easasAlert({
              summary: "JSON is NOT Valid",
              detail: (error as Error).message,
              severity: "error",
            });
        }
    };

    useEffect(() => {
        if (activeIndex === 1) {
            formatJSON();
        }
    }, [activeIndex]);

    return (
        <div style={{width: '100%', padding: '0 1rem 0 1rem', position: "relative"}}>
            <div className="p-fluid p-formgrid p-grid">
                <div className="p-field p-col">
                    <label htmlFor="title">Title</label>
                    <InputText id="title" type="text" value={configuratorState.title}
                               onChange={(e) => setConfiguratorState((prevState) => ({
                                   ...prevState,
                                   title: e.target.value
                               }))}/>
                </div>
            </div>

            <div className="p-fluid p-formgrid p-grid">
                <div className="p-field p-col">
                    <label htmlFor="type">Component type</label>
                    <Dropdown
                        value={configuratorState.componentType}
                        options={componentTypes}
                        filter
                        onChange={(e) => componentSelectorHandler(e.target.value)}
                        optionLabel="name"
                        optionValue="id"
                        placeholder="Select a component type"
                    />
                </div>
                <div className="p-field p-col">
                    <label htmlFor="width">Width</label>
                    <InputNumber
                        inputId="width"
                        min={2}
                        max={12}
                        showButtons
                        value={configuratorState.width}
                        onValueChange={(e) => setConfiguratorState((prevState) => ({...prevState, width: e.value}))}
                    />
                </div>
            </div>

            <TabView activeIndex={activeIndex} onTabChange={(e) => setActiveIndex(e.index)}>
                {SelectedComponent && isJSON(configuratorState.jsonConfig) && (
                    <TabPanel header="Editor">
                        <Suspense fallback={<ProgressSpinner/>}>
                            <SelectedComponent config={JSON.parse(configuratorState.jsonConfig as string)}
                                               onConfigChange={onJSONChange}/>
                        </Suspense>
                    </TabPanel>
                )}
                <TabPanel header="Edit RAW JSON">
                    <div className="p-fluid p-formgrid p-grid">
                        <div className="p-field p-col">
                            <Editor
                                height="420px"
                                defaultLanguage="json"
                                value={configuratorState.jsonConfig}
                                onChange={(e) => setConfiguratorState((prevState) => ({...prevState, jsonConfig: e}))}
                                onValidate={(markers) => {
                                    markers[0] &&
                                      easasAlert({
                                        summary: "JSON is NOT Valid",
                                        detail: markers[0].message,
                                        severity: "error",
                                      });
                                }}
                                theme="vs-dark"
                            />
                        </div>
                    </div>
                </TabPanel>
            </TabView>
        </div>
    );
};

export default DashboardComponentConfigurator;
